import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import {
  CHECPLUS_MODULE_NAMES,
  NEW,
  TEST,
  DESIGN,
  RETURNED,
  ACTIVE,
  FEATURE,
  FEATURE_COLLECTIONS,
  DAIMLER,
  CUSTOM,
  OBSOLETE,
} from 'utils/constants'
import strings from 'utils/strings'
import { reject } from 'ramda'
import { displayModuleName } from 'i18n/displayConstants'
import { setChipFilters, submitSearch, resetChipFilters } from 'actions/search'
import { ChipTagMenu, Row } from 'components/patterns'
import { useSelector } from 'hooks/redux'
// Bug 538201: getCanViewCollectionRevision removed — Collection Status filter is display-level only
import { selectChipFeatureFilterTerm } from 'selectors/search'
import {
  StyledFilterItemContainer,
  StyledModuleFilterCol,
  ModuleLabel,
  StyledStatusFilterCol,
  StyledButtonCol,
  StyledButton,
} from './AdvancedFeaturesChipFilters.style'

export const createModuleOptions = (filterModules) => {
  const moduleNames = filterModules || Object.values(CHECPLUS_MODULE_NAMES)
  return moduleNames.map((name) => ({
    key: name,
    value: name,
    title: displayModuleName(name),
  }))
}

const allStatuses = [DESIGN, TEST, ACTIVE, OBSOLETE]
const collectionTypeOptions = [DAIMLER, CUSTOM]
// Collection Status options for Feature Collections (PBI 150666)
const collectionStatusOptions = [DESIGN, ACTIVE, OBSOLETE]
const statusOptions = allStatuses.map((status) => ({
  key: status,
  value: status,
  title: status,
}))

// Reverse-map Redux-stored statuses (which use NEW/RETURNED instead of DESIGN)
// back to the display-layer values used by ChipTagMenu.
// e.g. ['New', 'Returned', 'Test'] → ['Design', 'Test']
const toDisplayStatuses = (storedStatuses) => {
  const hasDesign =
    storedStatuses.includes(NEW) || storedStatuses.includes(RETURNED)
  const others = storedStatuses.filter((s) => s !== NEW && s !== RETURNED)
  return hasDesign ? [DESIGN, ...others] : others
}

const filtersEqualApplied = ({ moduleNames, statuses, appliedFilters }) =>
  moduleNames.length === appliedFilters.moduleNames.length &&
  statuses.length === appliedFilters.statuses.length &&
  appliedFilters.statuses.every((status) => statuses.includes(status)) &&
  appliedFilters.moduleNames.every((moduleType) =>
    moduleNames.includes(moduleType)
  )

const AdvancedFeaturesChipFilters = ({
  searchString,
  isSearching,
  isHorizontalLayout,
  collectionName,
  filterModules,
}) => {
  const isFeatureCollectionsType = collectionName === FEATURE_COLLECTIONS

  // When filterModules is provided, restrict available ECU chips to that list.
  // This supports the Feature Collection editing context where only XMC3/XMC4 are valid (CL-15).
  const defaultModuleNames =
    filterModules || Object.values(CHECPLUS_MODULE_NAMES)
  const defaultFilters = useMemo(
    () => ({
      moduleNames: defaultModuleNames,
      statuses: allStatuses,
    }),
    // defaultModuleNames is either a stable prop array or a stable Object.values result;
    // we use JSON.stringify to keep the memo stable across identity-equal arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(defaultModuleNames)]
  )

  const dispatch = useDispatch()

  // Restore chip visual state from Redux so returning from a feature detail page
  // via the breadcrumb preserves the user's previous ECU/Status selection.
  // Falls back to defaultFilters when Redux has nothing (fresh page load or after reset).
  const reduxChipFilters = useSelector(selectChipFeatureFilterTerm, FEATURE)
  const reduxModuleNames = reduxChipFilters?.queries?.moduleNames
  const reduxStoredStatuses = reduxChipFilters?.queries?.statuses

  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)
  const [moduleNames, setModuleNames] = useState(
    reduxModuleNames ?? defaultFilters.moduleNames
  )
  const [statuses, setStatuses] = useState(
    reduxStoredStatuses
      ? toDisplayStatuses(reduxStoredStatuses)
      : defaultFilters.statuses
  )
  const [collectionTypes, setCollectionTypes] = useState(collectionTypeOptions)
  // Collection Status filter state (PBI 150666)
  const [collectionStatuses, setCollectionStatuses] = useState(
    collectionStatusOptions
  )

  // Keep chip visual state in sync with Redux chipFilters.
  // This handles two cases:
  //   1. Normal navigation: parent dispatches resetChipFilters({}) after mount →
  //      reduxChipFilters becomes empty → reset local state AND appliedFilters to defaults.
  //   2. Breadcrumb return / user chip interaction: Redux has queries →
  //      sync visual chip state only. appliedFilters is intentionally NOT updated here
  //      because on user chip interaction it should remain at "last submitted" so the
  //      Apply button correctly shows as enabled. On breadcrumb return the existing
  //      isSearching useEffect will align appliedFilters once the search runs.
  useEffect(() => {
    if (!reduxChipFilters || !reduxChipFilters.queries) {
      setModuleNames(defaultFilters.moduleNames)
      setStatuses(defaultFilters.statuses)
      setAppliedFilters(defaultFilters)
    } else {
      const storedModules = reduxChipFilters.queries.moduleNames
      const storedStatuses = reduxChipFilters.queries.statuses
      if (storedModules) setModuleNames(storedModules)
      if (storedStatuses) setStatuses(toDisplayStatuses(storedStatuses))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxChipFilters])

  // Filtering is a 2-step process: selecting values to filter on, then clicking the searck or "apply filters" button.
  // We track the 'applied' filters separately, so we can compare the current selection with what's been applied.
  // and disable/enable the apply & clear buttons accordingly.

  useEffect(() => {
    if (
      isSearching &&
      !filtersEqualApplied({
        moduleNames,
        statuses,
        appliedFilters,
        collectionTypes,
      })
    ) {
      setAppliedFilters({ moduleNames, statuses, collectionTypes })
    }
  }, [appliedFilters, isSearching, moduleNames, statuses, collectionTypes])

  const setParsedFilterStatus = (statusData) => {
    const isDesign = (status) => status === DESIGN
    const parsedStatuses = reject(isDesign, statusData)
    const filterStatuses = statusData.includes(DESIGN)
      ? [...parsedStatuses, NEW, RETURNED]
      : [...parsedStatuses]
    return filterStatuses
  }

  // When filterModules constrains the visible chip options (e.g., collection
  // editing restricts to XMC3/XMC4), dispatch the initial chip filters into
  // Redux on mount so the search saga includes the module constraint from the
  // very first search — not only after the user explicitly changes a filter.
  // Without this, the first search request goes out without module restrictions
  // even though the UI already shows only the restricted chips selected.
  useEffect(() => {
    if (filterModules) {
      dispatch(
        setChipFilters(
          {
            queries: {
              moduleNames: defaultFilters.moduleNames,
              statuses: setParsedFilterStatus(defaultFilters.statuses),
            },
          },
          FEATURE
        )
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectModuleType = (moduleValue) => {
    setModuleNames(moduleValue)
    const filterStatuses = setParsedFilterStatus(statuses)
    dispatch(
      setChipFilters(
        { queries: { moduleNames: moduleValue, statuses: filterStatuses } },
        FEATURE
      )
    )
  }

  const handleSelectCollectionType = (collectionTypeValue) => {
    setCollectionTypes(collectionTypeValue)
    dispatch(
      setChipFilters(
        {
          queries: { collectionTypes: collectionTypeValue, collectionStatuses },
        },
        FEATURE_COLLECTIONS
      )
    )
  }

  // Handler for Collection Status filter (PBI 150666)
  const handleSelectCollectionStatus = (collectionStatusValue) => {
    setCollectionStatuses(collectionStatusValue)
    dispatch(
      setChipFilters(
        {
          queries: {
            collectionTypes,
            collectionStatuses: collectionStatusValue,
          },
        },
        FEATURE_COLLECTIONS
      )
    )
  }

  const handleSelectStatus = (statusValue) => {
    setStatuses(statusValue)
    const filterStatuses = setParsedFilterStatus(statusValue)
    dispatch(
      setChipFilters(
        { queries: { moduleNames, statuses: filterStatuses } },
        FEATURE
      )
    )
  }

  const resetFilters = () => {
    setModuleNames(defaultFilters.moduleNames)
    setStatuses(defaultFilters.statuses)
    setCollectionTypes(collectionTypeOptions)
    setCollectionStatuses(collectionStatusOptions)
    const filterStatuses = setParsedFilterStatus(defaultFilters.statuses)
    dispatch(
      resetChipFilters(
        {
          queries: !isFeatureCollectionsType
            ? {
                moduleNames: defaultFilters.moduleNames,
                statuses: filterStatuses,
              }
            : {
                collectionTypes: collectionTypeOptions,
                collectionStatuses: collectionStatusOptions,
              },
        },
        collectionName || FEATURE
      )
    )
  }

  const filtersEqualDefault = () =>
    !isFeatureCollectionsType
      ? moduleNames.length === defaultFilters.moduleNames.length &&
        statuses.length === defaultFilters.statuses.length
      : collectionTypes.length === collectionTypeOptions.length &&
        collectionStatuses.length === collectionStatusOptions.length

  const applyFeatureFilters = () => {
    if (searchString.length > 0) {
      setAppliedFilters({ moduleNames, statuses })
      dispatch(submitSearch(FEATURE, true))
    }
  }

  const moduleOptions = createModuleOptions(filterModules)

  return (
    <>
      {!isFeatureCollectionsType ? (
        <>
          <Row>
            <StyledModuleFilterCol
              span={24}
              isHorizontalLayout={isHorizontalLayout}
            >
              <StyledFilterItemContainer
                className="filter-item-container"
                isHorizontalLayout={isHorizontalLayout}
              >
                <ModuleLabel isHorizontalLayout={isHorizontalLayout}>
                  {strings.ecu}:
                </ModuleLabel>
                <ChipTagMenu
                  value={moduleNames}
                  options={moduleOptions}
                  onChange={handleSelectModuleType}
                  placeholder={strings.search_status_required}
                />
              </StyledFilterItemContainer>
            </StyledModuleFilterCol>
          </Row>
          <Row>
            <StyledStatusFilterCol
              span={24}
              isHorizontalLayout={isHorizontalLayout}
            >
              <StyledFilterItemContainer
                className="filter-item-container"
                isHorizontalLayout={isHorizontalLayout}
              >
                <label>{strings.status}:</label>
                <ChipTagMenu
                  value={statuses}
                  options={statusOptions}
                  onChange={handleSelectStatus}
                  placeholder={strings.search_status_required}
                />
              </StyledFilterItemContainer>
            </StyledStatusFilterCol>
          </Row>
        </>
      ) : (
        <>
          <StyledStatusFilterCol
            span={24}
            isHorizontalLayout={isHorizontalLayout}
            noSpacing
          >
            <StyledFilterItemContainer
              className="filter-item-container"
              isHorizontalLayout={isHorizontalLayout}
            >
              <label>{strings.collection_type}:</label>
              <ChipTagMenu
                value={collectionTypes}
                options={collectionTypeOptions.map((item) => ({ value: item }))}
                onChange={handleSelectCollectionType}
              />
            </StyledFilterItemContainer>
          </StyledStatusFilterCol>
          {/* Collection Status Filter (PBI 150666) */}
          {/* Bug 538201: No permission gate — filter is display-level (client-side only) */}
          <StyledStatusFilterCol
            span={24}
            isHorizontalLayout={isHorizontalLayout}
            noSpacing
          >
            <StyledFilterItemContainer
              className="filter-item-container"
              isHorizontalLayout={isHorizontalLayout}
            >
              <label>{strings.collection_status}:</label>
              <ChipTagMenu
                value={collectionStatuses}
                options={collectionStatusOptions.map((item) => ({
                  value: item,
                }))}
                onChange={handleSelectCollectionStatus}
              />
            </StyledFilterItemContainer>
          </StyledStatusFilterCol>
        </>
      )}
      <StyledButtonCol span={24} isHorizontalLayout={isHorizontalLayout}>
        <StyledButton
          size="small"
          onClick={resetFilters}
          text={
            !isFeatureCollectionsType ? strings.reset : strings.reset_filters
          }
          disabled={filtersEqualDefault()}
          {...(isFeatureCollectionsType ? { float: 'right' } : {})}
        />
        {!isFeatureCollectionsType && (
          <StyledButton
            type="primary"
            size="small"
            onClick={applyFeatureFilters}
            text={strings.apply_filters}
            disabled={
              filtersEqualApplied({
                moduleNames,
                statuses,
                appliedFilters,
              }) ||
              !searchString ||
              !statuses.length
            }
          />
        )}
      </StyledButtonCol>
    </>
  )
}

AdvancedFeaturesChipFilters.propTypes = {
  applyFilters: PropTypes.func,
  searchString: PropTypes.string,
  collectionName: PropTypes.oneOf([FEATURE, FEATURE_COLLECTIONS]),
  isSearching: PropTypes.bool,
  addToSearchRequestBody: PropTypes.bool,
  isHorizontalLayout: PropTypes.bool,
  filterModules: PropTypes.arrayOf(PropTypes.string),
}

export default AdvancedFeaturesChipFilters
